import { FC } from 'react';
import {
  Box,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Spinner,
  Center,
  Badge,
  Text,
  HStack,
} from '@chakra-ui/react';
import { LeadWithFunnel, SalesFunnel } from '../../types';

export interface LeadsListViewProps {
  leads: LeadWithFunnel[];
  loading?: boolean;
  onLeadClick?: (lead: LeadWithFunnel) => void;
  funnel?: SalesFunnel | null;
}

export const LeadsListView: FC<LeadsListViewProps> = ({
  leads,
  loading = false,
  onLeadClick,
  funnel,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const getStatusColor = (status?: string | null) => {
    switch (status) {
      case 'converted':
        return 'green';
      case 'discarded':
        return 'red';
      default:
        return 'blue';
    }
  };

  const getStepColor = (stepId?: string | null): string => {
    if (!funnel || !stepId) return '#CBD5E0';
    const step = funnel.steps.find(s => s.id === stepId);
    return step?.color || '#CBD5E0';
  };

  const getStepName = (stepId?: string | null): string => {
    if (!funnel || !stepId) return 'Unassigned';
    const step = funnel.steps.find(s => s.id === stepId);
    return step?.name || 'Unassigned';
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box w="100%" bg={bgColor} border="1px" borderColor={borderColor} borderRadius="lg" overflowX="auto">
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Phone</Th>
            <Th>Status</Th>
            <Th>Created</Th>
          </Tr>
        </Thead>
        <Tbody>
          {leads.map((lead) => (
            <Tr
              key={lead.id}
              cursor={onLeadClick ? 'pointer' : 'default'}
              onClick={() => onLeadClick?.(lead)}
              _hover={onLeadClick ? { bg: useColorModeValue('gray.50', 'gray.700') } : {}}
            >
              <Td>
                <HStack spacing={2}>
                  <Box
                    w="12px"
                    h="12px"
                    borderRadius="full"
                    bg={getStepColor(lead.current_step_id)}
                    flexShrink={0}
                    title={getStepName(lead.current_step_id)}
                  />
                  <Text>{lead.name}</Text>
                </HStack>
              </Td>
              <Td>{lead.email}</Td>
              <Td>{lead.phone || '-'}</Td>
              <Td>
                {lead.status && (
                  <Badge colorScheme={getStatusColor(lead.status)}>
                    {lead.status}
                  </Badge>
                )}
              </Td>
              <Td>
                {lead.created_at
                  ? new Date(lead.created_at).toLocaleDateString()
                  : '-'}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {leads.length === 0 && (
        <Center h="200px">
          <VStack spacing={2}>
            <Text color={useColorModeValue('gray.500', 'gray.400')}>
              No leads found
            </Text>
          </VStack>
        </Center>
      )}
    </Box>
  );
};

